import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Test } from '@bugbug/core/types/tests';
import { selectSingleProjectId } from '~/modules/project/project.selectors';
import { useAppSelector } from '~/modules/store';
import analytics from '~/services/analytics';
import toasts from '~/services/toasts';

import { testsApi } from '../../tests.api';

export const useDeleteTests = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'tests.actions.delete' });
  const projectId = useAppSelector(selectSingleProjectId);
  const [deleteTests, deleteTestsState] = testsApi.useDeleteTestsMutation();

  const handleDeleteTests = useCallback(
    async ({ ids }: { ids?: Test['id'][] } = {}) => {
      if (ids) {
        await deleteTests({ ids }).unwrap();

        toasts.showSuccess({
          content:
            ids.length === 1
              ? t('deleteSuccessInfo', 'Test has been deleted successfully.')
              : t('selectedDeleteSuccessInfo', 'Tests have been deleted successfully.'),
        });
      } else {
        await deleteTests({ projectId }).unwrap();

        toasts.showSuccess({
          content: t('allDeleteSuccessInfo', 'All tests have been deleted successfully.'),
        });
      }

      analytics.trackEvent(ids?.length === 1 ? 'test_deleted' : 'bulk_tests_deleted');
    },
    [deleteTests, projectId, t],
  );

  return useMemo(
    () => [handleDeleteTests, deleteTestsState] as const,
    [handleDeleteTests, deleteTestsState],
  );
};
